<script>
    import API from '../../../../api';
    import isNil from 'lodash/isNil';
    import {
        RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT,
        RESERVATION_TECHRESERVATION_DELETE_FEE_ENDPOINT,
    } from '../../../../endpoints';
    import TechInfoTable from './tech-info-table.vue';
    import ActionsHeaderRendererAddItem from './actions-header-renderer-add-item.vue';
    import ActionsCellRendererRemoveItem from './actions-cell-renderer-delete-item.vue';
    import { toAmountDisplay } from '../../../../utils/numbers';

    export default {
        extends: TechInfoTable,
        components: {
            actionsHeaderRendererAddItem: ActionsHeaderRendererAddItem,
            actionsCellRendererRemoveItem: ActionsCellRendererRemoveItem,
        },
        props: {
            taxOptions: {
                type: Array,
                default: () => [],
            },
            depositOptions: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                addModalId: `add-fees-modal-${this.reservationId}`,
                editAutoWidthColumns: ['parent'],
                modalTitle: 'Add Fee',
                modalLabel: 'Fee',
                module: 'fee',
                columnDefs: [
                    {
                        headerName: '',
                        maxWidth: 60,
                        flex: 1,
                        sortable: false,
                        resizable: false,
                        cellClass: 'tech-info-remove-item',
                        headerComponent: 'actionsHeaderRendererAddItem',
                        headerComponentParams: {
                            addItem: () => {
                                this.showAddItem(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT);
                            },
                            conditional: true,
                        },
                        cellRenderer: 'actionsCellRendererRemoveItem',
                        cellRendererParams: {
                            deleteItem: id => {
                                this.deleteItem(id, RESERVATION_TECHRESERVATION_DELETE_FEE_ENDPOINT);
                            },
                        },
                    },
                    {
                        headerName: 'Fees',
                        sortable: false,
                        field: 'title',
                        type: 'editableColumn',
                        editable: ({ data }) => isNil(data.source_id),
                        cellClass: ({ data }) => (isNil(data.source_id) ? 'text-primary lx-editable-cell' : ''),
                        valueSetter: ({ node, data, newValue }) => {
                            if (data.title != newValue) {
                                data.title = newValue;
                                this.changeFeeTitle(data.id, newValue);
                            }
                        },
                        resizable: true,
                        autoWidthExclude: true,
                        minWidth: 200,
                        valueParser: (value) => {
                            return value.title;
                        },
                    },
                    {
                        headerName: 'Related',
                        sortable: false,
                        field: 'parent',
                        flex: 1,
                        cellRenderer: ({ data, value }) => (!isNil(data.parent) ? data.parent_name : 'N/A'),
                        editable: ({ data }) => isNil(data.source_id) && this.depositOptions.length > 1,
                        cellClass: ({ data }) =>
                            isNil(data.source_id) && this.depositOptions.length > 1
                                ? 'text-primary lx-editable-cell'
                                : '',
                        cellEditor: 'selectCellEditor',
                        cellEditorParams: () => ({
                            options: this.depositOptions,
                        }),
                        valueSetter: ({ node, data, newValue }) => {
                            if (data.parent != newValue) {
                                this.changeFeeParent(data.id, newValue);
                            }
                        },
                        valueParser: (value) => {
                            return value.parent;
                        },
                        resizable: false,
                    },
                    {
                        headerName: 'Number',
                        field: 'number',
                        cellRenderer: ({ data, value }) => (data.is_pet_fee ? value : 'N/A'),
                        sortable: false,
                        cellEditor: 'selectCellEditor',
                        cellEditorParams: () => ({
                            options: [1, 2, 3, 4, 5, 6].map(item => ({
                                value: item,
                                text: item,
                            })),
                        }),
                        editable: ({ data }) => data.is_pet_fee,
                        cellClass: ({ data }) => (data.is_pet_fee ? 'text-primary lx-editable-cell' : ''),
                        valueSetter: ({ node, data, newValue }) => {
                            if (data.number != newValue) {
                                data.number = newValue;
                                this.changeFeeNumber(data.id, newValue);
                            }
                        },
                        valueParser: (value) => {
                            return value.number;
                        },
                        resizable: false,
                    },
                    {
                        headerName: 'Taxable',
                        sortable: false,
                        type: 'yesNoColumnNew',
                        field: 'taxable',
                        flex: 1,
                        editable: ({ data }) => {
                            return this.taxOptions.length > 0;
                        },
                        valueGetter: ({ data }) => {
                            return data.taxable && this.taxOptions.length > 0;
                        },                        
                        cellEditor: 'multiSelectCellEditor',
                        cellEditorParams: ({ data }) => ({
                            options: this.taxOptions,
                            values: data.taxes,
                        }),
                        cellClass: ({ data }) =>
                            this.taxOptions.length > 0 ? 'lx-editable-yes-no-cell' : 'lx-yes-no-cell',
                        valueSetter: ({ node, data, newValue }) => {
                            if (newValue != data.taxes) {
                                this.changeFeeTaxes(data.id, newValue);
                            }
                        },
                        resizable: false,
                    },
                    {
                        type: 'editableColumn',
                        headerName: 'Amount',
                        field: 'total_net',
                        cellRenderer: ({ data, value }) =>
                            toAmountDisplay({
                                amount: value,
                                currency: this.userCurrencySymbol,
                            }),
                        sortable: false,
                        cellEditorParams: { isDecimal: true, select: true, isNegative: true },
                        editable: true,
                        cellClass: ({ data }) => {
                            return `lx-editable-cell ag-right-aligned-cell text-primary ${
                                data.is_overriden ? 'font-weight-bold' : ''
                            }`;
                        },
                        valueSetter: ({ node, data, newValue }) => {
                            data.total_net = newValue;
                            this.changeFeeValue(data.id, newValue);
                        },
                        valueParser: (value) => {
                            return value.total_net;
                        },
                        resizable: false,
                        headerClass: 'ag-right-aligned-header',
                    },
                ],
            };
        },
        methods: {
            async addOneTimeFee() {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {});
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not add fee.` });
                } finally {
                    this.busy = false;
                }
            },
            async changeFeeValue(id, value) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        value: value,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not set fee value.` });
                } finally {
                    this.busy = false;
                }
            },
            async changeFeeTitle(id, value) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        title: value,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not set fee title.` });
                } finally {
                    this.busy = false;
                }
            },
            async changeFeeParent(id, value) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        parent: value,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not set fee relationship.` });
                } finally {
                    this.busy = false;
                }
            },
            async changeFeeTaxes(id, value) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        taxes: value,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not set fee taxes.` });
                } finally {
                    this.busy = false;
                }
            },
            async changeFeeNumber(id, value) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_TECHRESERVATION_CHANGE_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        number: value,
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not set fee taxes.` });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

